import React, { useState, useRef, useEffect } from 'react';
import AiChatMessage from './AiChatMessage';
import ChatInputForm from './ChatInputForm';
import makeCloudinaryUrlForPhoto from '../../afa-photo';

/**
 * Full-screen modal for AI image editing
 * Features:
 * - Display original product image
 * - Chat interface for iterative AI image generation
 * - Toggle to use original image as reference or start from scratch
 * - Chat history with previous sessions
 * - "Replace Main" button on generated images
 */
function AiImageEditorModal({
  isOpen,
  onClose,
  product,
  currentChat,
  chatHistory,
  onSendMessage,
  onNewChat,
  onSelectChat,
  onReplaceMain,
  isLoading,
}) {
  const [useReference, setUseReference] = useState(true);
  const chatContainerRef = useRef(null);
  
  // Get product photo
  const photo = product?.get ? product.get('photo') : product?.photo;
  const productName = product?.get ? product.get('name.en') : product?.name?.en;
  
  // Generate image URL for display
  const imageUrl = photo ? makeCloudinaryUrlForPhoto(photo, { width: 1000, height: 1000 }) : null;
  
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [currentChat?.messages]);
  
  // Handle sending a message (called from ChatInputForm)
  const handleSendMessage = (promptText) => {
    onSendMessage(promptText, useReference);
  };
  
  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-50 flex"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal content */}
      <div className="relative flex w-full h-full bg-white">
        {/* Left sidebar - Chat history */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-700">Chat History</h3>
          </div>
          
          {/* New chat button */}
          <button
            onClick={onNewChat}
            className="
              m-2 p-2
              text-sm font-medium
              bg-purple-500 hover:bg-purple-600
              text-white
              rounded-md
              transition-colors
            "
          >
            + New Chat
          </button>
          
          {/* Chat list */}
          <div className="flex-1 overflow-y-auto p-2">
            {chatHistory && chatHistory.map((chat) => (
              <button
                key={chat.id}
                onClick={() => onSelectChat(chat.id)}
                className={`
                  w-full text-left p-2 mb-1 rounded-md text-sm
                  transition-colors
                  ${currentChat?.id === chat.id 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'hover:bg-gray-100 text-gray-600'
                  }
                `}
              >
                <div className="font-medium truncate">
                  {chat.messages?.[0]?.text?.substring(0, 30) || 'New chat'}
                  {chat.messages?.[0]?.text?.length > 30 ? '...' : ''}
                </div>
                <div className="text-xs text-gray-400">
                  {chat.createdAt ? new Date(chat.createdAt.toDate?.() || chat.createdAt).toLocaleDateString() : 'Just now'}
                </div>
                <div className="text-xs text-gray-400">
                  {chat.usedReference ? '📷 With reference' : '✨ From scratch'}
                </div>
              </button>
            ))}
            
            {(!chatHistory || chatHistory.length === 0) && (
              <p className="text-sm text-gray-400 text-center py-4">
                No chat history yet
              </p>
            )}
          </div>
        </div>
        
        {/* Main content area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-gray-800">
                AI Image Editor
              </h2>
              {productName && (
                <span className="text-sm text-gray-500">
                  {productName}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Content area */}
          <div className="flex-1 flex overflow-hidden">
            {/* Original image panel */}
            <div className="w-80 border-r border-gray-200 p-4 flex flex-col">
              <h3 className="font-semibold text-gray-700 mb-3">Original Image</h3>
              {imageUrl && imageUrl !== 'no-pic' ? (
                <div className="flex-1 flex items-start justify-center">
                  <img
                    src={imageUrl}
                    alt={productName || 'Product'}
                    className="max-w-full max-h-64 rounded-lg shadow-md object-contain"
                  />
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center bg-gray-100 rounded-lg">
                  <p className="text-gray-400">No image</p>
                </div>
              )}
              
              {/* Reference toggle */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useReference}
                    onChange={(e) => setUseReference(e.target.checked)}
                    disabled={currentChat?.messages?.length > 0}
                    className="
                      w-4 h-4
                      text-purple-500
                      rounded
                      focus:ring-purple-500
                      disabled:opacity-50
                    "
                  />
                  <span className={`text-sm ${currentChat?.messages?.length > 0 ? 'text-gray-400' : 'text-gray-700'}`}>
                    Use original as reference
                  </span>
                </label>
                <p className="text-xs text-gray-400 mt-1">
                  {currentChat?.messages?.length > 0 
                    ? 'Cannot change mid-chat. Start a new chat to change.'
                    : useReference 
                      ? 'AI will edit this image based on your prompts'
                      : 'AI will generate from scratch (text only)'
                  }
                </p>
              </div>
            </div>
            
            {/* Chat area */}
            <div className="flex-1 flex flex-col">
              {/* Messages */}
              <div 
                ref={chatContainerRef}
                className="flex-1 overflow-y-auto p-4"
              >
                {currentChat?.messages?.length > 0 ? (
                  currentChat.messages.map((message, index) => (
                    <AiChatMessage
                      key={index}
                      message={message}
                      onReplaceMain={onReplaceMain}
                    />
                  ))
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center text-gray-400">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-lg font-medium">Start a conversation</p>
                      <p className="text-sm mt-1">
                        {useReference 
                          ? 'Describe how you want to edit the image'
                          : 'Describe the image you want to generate'
                        }
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Loading indicator */}
                {isLoading && (
                  <AiChatMessage
                    message={{ role: 'model', isLoading: true }}
                  />
                )}
              </div>
              
              {/* Input area - isolated component to prevent re-renders on typing */}
              <ChatInputForm
                onSendMessage={handleSendMessage}
                isLoading={isLoading}
                useReference={useReference}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AiImageEditorModal;
